/**
 * Daily Brief — the pure half: the preset board, "preset plus yours", the
 * editing rules, and the scales the widgets draw with.
 *
 * The JAS board of small market widgets (`docs/plans/daily-brief.md`, mock
 * `docs/daily-brief-mock.html`). One `briefBoard` section holds both halves:
 * `preset`, what the layout's author arranged and published, and `mine`, a
 * member's own board on top of it. The maths is on the server
 * (`qf/daily_brief.py`); the panel is `components/BriefBoardView.tsx`.
 */
import {
  mintUlid,
  type BriefBoardConfig,
  type BriefBoardState,
  type BriefList,
  type BriefListItem,
  type BriefWidget,
  type Layout,
  type Section,
} from "@qufolio/model";

export type WidgetType = BriefWidget["type"];

/** A widget's header title, its smallest `[w, h]`, and its `[w, h]` when added (the mock's sizes). */
export const WIDGET_META: Record<WidgetType, { title: string; min: [number, number]; size: [number, number] }> = {
  market: { title: "Market Performance", min: [3, 12], size: [4, 20] },
  sectors: { title: "Sector Performance", min: [6, 6], size: [12, 11] },
  daily: { title: "Daily Performance", min: [6, 4], size: [12, 8] },
  etf: { title: "ETF Performance", min: [6, 6], size: [12, 13] },
  nhnl: { title: "New Highs / New Lows", min: [4, 5], size: [6, 7] },
  race: { title: "Sector Race", min: [4, 10], size: [8, 13] },
};

/** The board's grid: 12 columns, 28 px rows, 10 px between cards. */
export const GRID = { cols: 12, rowHeight: 28, margin: 10 } as const;

/** Closing range at or above this is green, at or below `RANGE_LOW` red, grey between. Provisional — owner's call. */
export const RANGE_HIGH = 66;
export const RANGE_LOW = 33;

/** Where a member's own symbols go unless they name a category (owner, 2026-10-17). */
export const USER_CATEGORY = "My symbols";

/** Every widget refetches on this beat. */
export const REFRESH_MS = 60_000;

export type ListName = "daily" | "etf";

// ── the preset ───────────────────────────────────────────────────────────────

const PRESET_WIDGETS: ReadonlyArray<Omit<BriefWidget, "id">> = [
  { type: "market", x: 0, y: 0, w: 4, h: 16 },
  { type: "sectors", x: 4, y: 0, w: 8, h: 16 },
  { type: "daily", x: 0, y: 16, w: 12, h: 8 },
  { type: "etf", x: 0, y: 24, w: 12, h: 14 },
  { type: "nhnl", x: 0, y: 38, w: 6, h: 7 },
];

const PRESET_DAILY: ReadonlyArray<BriefListItem> = [
  { symbol: "DIA", label: "Dow 30" },
  { symbol: "IBIT" },
  { symbol: "IWM", label: "Russell 2000" },
  { symbol: "QQQ", label: "Nasdaq 100" },
  { symbol: "SPY", label: "S&P 500" },
  { symbol: "UUP" },
  { symbol: "VXX" },
];

/** ETF Performance starts with the themes; the sectors are Sector Performance's (decision 6). */
const PRESET_THEMES = (
  "AIQ ASEA BLOK BOTZ BUG CHIQ CIBR CLOU COPX CTEC DRIV DTCR EBIZ EDOC FDN FINX GDX GNOM GXTG HERO " +
  "IGV ITA ITB IYT IYZ KWEB LIT MILN MLPX MSOS NORW PAVE PEJ PFFD QYLD RNRG ROBO SIL SMH SNSR SOCL " +
  "TAN URA XBI XME XOP XRT"
).split(" ");

/** The board a new layout starts with, every widget a fresh id. */
export function presetState(): BriefBoardState {
  return {
    widgets: PRESET_WIDGETS.map((w) => ({ ...w, id: mintUlid() })),
    lists: {
      daily: { items: PRESET_DAILY.map((i) => ({ ...i })), categories: [] },
      etf: { items: PRESET_THEMES.map((symbol) => ({ symbol, category: "Theme" })), categories: ["Theme"] },
    },
  };
}

/** A new layout holding one Daily Brief across the whole grid — `bucketsBriefLayout`'s shape. */
export function briefBoardLayout(name: string): Layout {
  return {
    id: mintUlid(),
    name,
    rows: 1,
    columns: 1,
    rowSizes: ["1fr"],
    columnSizes: ["1fr"],
    sections: [
      {
        id: mintUlid(),
        kind: "briefBoard",
        row: 0,
        column: 0,
        title: "Daily Brief",
        config: { preset: presetState() },
      },
    ],
  };
}

const isState = (s: unknown): s is BriefBoardState =>
  !!s && typeof s === "object" && Array.isArray((s as BriefBoardState).widgets);

/** The section's config, a missing or broken preset replaced by the default one. */
export function briefBoardConfig(section: Section): BriefBoardConfig | undefined {
  if (section.kind !== "briefBoard") return undefined;
  const config = section.config as Partial<BriefBoardConfig> | undefined;
  const out: BriefBoardConfig = { preset: isState(config?.preset) ? config.preset : presetState() };
  if (isState(config?.mine)) {
    out.mine = config.mine;
    if (typeof config.mineBasedOn === "number") out.mineBasedOn = config.mineBasedOn;
  }
  return out;
}

// ── preset plus yours ────────────────────────────────────────────────────────

const key = (symbol: string) => symbol.trim().toUpperCase();

/**
 * A list as a widget shows it: the preset's items less the member's hidden
 * ones, then the member's added ones in their own category. Reading the
 * preset every time is what lets a symbol the author adds later reach a
 * member who already has a board of their own.
 */
export function resolvedList(
  preset: BriefList | undefined,
  mine: BriefList | undefined,
): { items: BriefListItem[]; categories: string[] } {
  const hidden = new Set((mine?.hidden ?? []).map(key));
  const items = (preset?.items ?? []).filter((i) => !hidden.has(key(i.symbol)));
  const seen = new Set(items.map((i) => key(i.symbol)));
  const categories = [...(preset?.categories ?? [])];
  for (const added of mine?.added ?? []) {
    if (seen.has(key(added.symbol))) continue;
    seen.add(key(added.symbol));
    const category = added.category || USER_CATEGORY;
    items.push({ ...added, category });
    if (!categories.includes(category)) categories.push(category);
  }
  return { items, categories };
}

/**
 * The member's added items that `resolvedList` actually draws as theirs: an
 * add the author has since published is the preset's row, not the member's.
 */
export function memberAdded(preset: BriefList | undefined, mine: BriefList | undefined): BriefListItem[] {
  const inPreset = new Set((preset?.items ?? []).map((i) => key(i.symbol)));
  const seen = new Set<string>();
  return (mine?.added ?? []).filter((i) => {
    const k = key(i.symbol);
    if (inPreset.has(k) || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * The categories a member may file their own symbols under: `USER_CATEGORY`
 * and the ones they made themselves — never the author's (decision 3).
 */
export function memberCategories(preset: BriefList | undefined, mine: BriefList | undefined): string[] {
  const theirs = authorCategories(preset);
  const out = [USER_CATEGORY];
  for (const i of mine?.added ?? []) {
    const c = i.category || USER_CATEGORY;
    if (!theirs.has(c) && !out.includes(c)) out.push(c);
  }
  return out;
}

const authorCategories = (preset: BriefList | undefined) =>
  new Set([...(preset?.categories ?? []), ...(preset?.items ?? []).flatMap((i) => (i.category ? [i.category] : []))]);

/**
 * The config as one viewer's board: a member's copy keeps `mine`; any other
 * board shows and edits the preset alone. A `mine` can reach a board that is
 * no member's copy — a layout saved as a template, say — and drawing it there
 * while every edit went to the preset made drags snap back.
 */
export function boardFor(config: BriefBoardConfig, member: boolean): BriefBoardConfig {
  return member || !config.mine ? config : resetToPreset(config);
}

/** What the board shows: the member's widgets if they have their own, and the lists resolved. */
export function effectiveState(config: BriefBoardConfig): BriefBoardState {
  const names = new Set([...Object.keys(config.preset.lists ?? {}), ...Object.keys(config.mine?.lists ?? {})]);
  const lists: Record<string, BriefList> = {};
  for (const name of names) lists[name] = resolvedList(config.preset.lists?.[name], config.mine?.lists?.[name]);
  return { widgets: config.mine?.widgets ?? config.preset.widgets, lists };
}

/** A copied group layout being viewed by a member: their edits go to `mine`, not the preset. */
export function isMemberCopy(layout: Layout, authoring: boolean): boolean {
  return !!layout.source && !authoring;
}

export interface EditContext {
  /** True when edits go to `mine` (`isMemberCopy`). */
  member: boolean;
  /** The layout's `source.version` — what a member's first edit is based on. */
  sourceVersion?: number;
}

/**
 * The state an edit applies to, and how to put it back. A member's first edit
 * starts `mine` from the widgets they were looking at, with empty list diffs.
 */
function edit(config: BriefBoardConfig, ctx: EditContext, apply: (state: BriefBoardState) => BriefBoardState): BriefBoardConfig {
  if (!ctx.member) return { ...config, preset: apply(config.preset) };
  if (config.mine) return { ...config, mine: apply(config.mine) };
  const mine: BriefBoardState = { widgets: config.preset.widgets.map((w) => ({ ...w })), lists: {} };
  return { ...config, mine: apply(mine), mineBasedOn: ctx.sourceVersion ?? 0 };
}

/** Replace the board's widgets — a drag or resize. */
export function setWidgets(config: BriefBoardConfig, ctx: EditContext, widgets: BriefWidget[]): BriefBoardConfig {
  return edit(config, ctx, (s) => ({ ...s, widgets }));
}

export function addWidget(config: BriefBoardConfig, ctx: EditContext, type: WidgetType): BriefBoardConfig {
  return edit(config, ctx, (s) => ({ ...s, widgets: [...s.widgets, placeWidget(s.widgets, type)] }));
}

export function removeWidget(config: BriefBoardConfig, ctx: EditContext, id: string): BriefBoardConfig {
  return edit(config, ctx, (s) => ({ ...s, widgets: s.widgets.filter((w) => w.id !== id) }));
}

/** Merge `settings` into one widget's — a toggle or a sort. */
export function setWidgetSettings(
  config: BriefBoardConfig,
  ctx: EditContext,
  id: string,
  settings: Record<string, unknown>,
): BriefBoardConfig {
  return edit(config, ctx, (s) => ({
    ...s,
    widgets: s.widgets.map((w) => (w.id === id ? { ...w, settings: { ...w.settings, ...settings } } : w)),
  }));
}

const withList = (state: BriefBoardState, name: ListName, list: BriefList): BriefBoardState => ({
  ...state,
  lists: { ...state.lists, [name]: list },
});

/**
 * Add a symbol to a list. The author's goes into the preset as given; a
 * member's goes into `added`, in `USER_CATEGORY` unless they typed one of
 * their own — an author's category is the author's, so naming one files it in
 * `USER_CATEGORY` (decision 3) — or, when it is a preset symbol they had
 * hidden, simply comes back.
 */
export function addSymbol(config: BriefBoardConfig, ctx: EditContext, list: ListName, item: BriefListItem): BriefBoardConfig {
  const symbol = key(item.symbol);
  if (!symbol) return config;
  const entry: BriefListItem = { ...item, symbol };
  if (!ctx.member) {
    return edit(config, ctx, (s) => {
      const cur = s.lists?.[list] ?? {};
      const items = (cur.items ?? []).filter((i) => key(i.symbol) !== symbol);
      const categories = [...(cur.categories ?? [])];
      if (entry.category && !categories.includes(entry.category)) categories.push(entry.category);
      return withList(s, list, { ...cur, items: [...items, entry], categories });
    });
  }
  const preset = config.preset.lists?.[list];
  const inPreset = (preset?.items ?? []).some((i) => key(i.symbol) === symbol);
  const category = entry.category && !authorCategories(preset).has(entry.category) ? entry.category : USER_CATEGORY;
  return edit(config, ctx, (s) => {
    const cur = s.lists?.[list] ?? {};
    const hidden = (cur.hidden ?? []).filter((h) => key(h) !== symbol);
    if (inPreset) return withList(s, list, { ...cur, hidden });
    const added = (cur.added ?? []).filter((i) => key(i.symbol) !== symbol);
    return withList(s, list, { ...cur, hidden, added: [...added, { ...entry, category }] });
  });
}

/** Take a symbol out: the author deletes it from the preset; a member drops one they added or hides a preset one. */
export function removeSymbol(config: BriefBoardConfig, ctx: EditContext, list: ListName, symbol: string): BriefBoardConfig {
  const k = key(symbol);
  if (!ctx.member) {
    return edit(config, ctx, (s) => {
      const cur = s.lists?.[list] ?? {};
      return withList(s, list, { ...cur, items: (cur.items ?? []).filter((i) => key(i.symbol) !== k) });
    });
  }
  const inPreset = (config.preset.lists?.[list]?.items ?? []).some((i) => key(i.symbol) === k);
  return edit(config, ctx, (s) => {
    const cur = s.lists?.[list] ?? {};
    const added = (cur.added ?? []).filter((i) => key(i.symbol) !== k);
    const hidden = cur.hidden ?? [];
    return withList(s, list, {
      ...cur,
      added,
      hidden: inPreset && !hidden.some((h) => key(h) === k) ? [...hidden, k] : hidden,
    });
  });
}

/** Bring back a preset symbol a member hid. The author has nothing hidden, so it is theirs a no-op. */
export function unhideSymbol(config: BriefBoardConfig, ctx: EditContext, list: ListName, symbol: string): BriefBoardConfig {
  const k = key(symbol);
  if (!ctx.member || !config.mine?.lists?.[list]?.hidden?.some((h) => key(h) === k)) return config;
  return edit(config, ctx, (s) => {
    const cur = s.lists?.[list] ?? {};
    return withList(s, list, { ...cur, hidden: (cur.hidden ?? []).filter((h) => key(h) !== k) });
  });
}

/** Reset to preset: the member's board and its version go. */
export function resetToPreset(config: BriefBoardConfig): BriefBoardConfig {
  const { mine: _mine, mineBasedOn: _basedOn, ...rest } = config;
  return rest;
}

export function hasMine(config: BriefBoardConfig): boolean {
  return !!config.mine;
}

/** "A newer preset is available": the member's board was taken from an older published version. */
export function newerPresetAvailable(config: BriefBoardConfig, sourceVersion?: number): boolean {
  return !!config.mine && (config.mineBasedOn ?? 0) < (sourceVersion ?? 0);
}

/**
 * A freshly published config with the member's board carried over — a publish
 * replaces a member's copy of the layout, and without this every publish would
 * wipe every member's board.
 */
export function carryMine(fresh: BriefBoardConfig, old: BriefBoardConfig | undefined): BriefBoardConfig {
  if (!old?.mine) return fresh;
  const out: BriefBoardConfig = { ...fresh, mine: old.mine };
  if (old.mineBasedOn !== undefined) out.mineBasedOn = old.mineBasedOn;
  return out;
}

/** A new widget at the left edge, below the lowest one, at its default size. */
export function placeWidget(widgets: readonly BriefWidget[], type: WidgetType): BriefWidget {
  const [w, h] = WIDGET_META[type].size;
  const y = Math.max(0, ...widgets.map((x) => x.y + x.h));
  return { id: mintUlid(), type, x: 0, y, w, h };
}

// ── the scales ───────────────────────────────────────────────────────────────

/** A column's scale: its largest |value|, 0 when it has none. */
export function columnMax(values: ReadonlyArray<number | null | undefined>): number {
  let max = 0;
  for (const v of values) if (typeof v === "number" && Number.isFinite(v)) max = Math.max(max, Math.abs(v));
  return max;
}

/**
 * A bar's width as a percent of the cell, 0–50: zero sits in the middle, so
 * the column's largest value fills its half. Positive draws right, negative left.
 */
export function barWidth(v: number | null | undefined, max: number): number {
  if (typeof v !== "number" || !Number.isFinite(v) || !(max > 0)) return 0;
  return Math.min(50, (50 * Math.abs(v)) / max);
}

/** A closing range's colour: up at `RANGE_HIGH` and over, down at `RANGE_LOW` and under. */
export function rangeTone(v: number | null | undefined): "up" | "down" | "flat" {
  if (typeof v !== "number" || !Number.isFinite(v)) return "flat";
  return v >= RANGE_HIGH ? "up" : v <= RANGE_LOW ? "down" : "flat";
}
